import { useState } from 'react';
import { Mail, Linkedin, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { toast } from '../hooks/use-toast';

const Contact = ({ personalInfo }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission - will be connected to backend later
    toast({
      title: "Message Sent!",
      description: "Thank you for reaching out. I'll get back to you soon.",
    });
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0D1117] mb-4">
            Let's Connect
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            I'm always interested in hearing about new opportunities and collaborations. Feel free to reach out!
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-bold text-[#0D1117] mb-6">Get in Touch</h3>
              <p className="text-gray-600 mb-8">
                Whether you're interested in discussing finance, business opportunities, or collaboration on social impact initiatives, I'd love to hear from you.
              </p>
            </div>

            {/* Contact Methods */}
            <div className="space-y-4">
              <Card className="border-gray-200 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#9A7B4F]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#9A7B4F]" size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0D1117]">Email</p>
                    <a
                      href={`mailto:${personalInfo.email}`}
                      className="text-gray-600 hover:text-[#9A7B4F] transition-colors"
                    >
                      {personalInfo.email}
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#9A7B4F]/10 flex items-center justify-center flex-shrink-0">
                    <Linkedin className="text-[#9A7B4F]" size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0D1117]">LinkedIn</p>
                    <a
                      href={personalInfo.socialLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-[#9A7B4F] transition-colors"
                    >
                      Connect on LinkedIn
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="border-gray-200 shadow-xl">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#0D1117] mb-2">
                    Name
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="border-gray-300 focus:border-[#9A7B4F] focus:ring-[#9A7B4F]"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#0D1117] mb-2">
                    Email
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                    className="border-gray-300 focus:border-[#9A7B4F] focus:ring-[#9A7B4F]"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-[#0D1117] mb-2">
                    Subject
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="What's this about?"
                    className="border-gray-300 focus:border-[#9A7B4F] focus:ring-[#9A7B4F]"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#0D1117] mb-2">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Your message..."
                    className="border-gray-300 focus:border-[#9A7B4F] focus:ring-[#9A7B4F]"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#9A7B4F] hover:bg-[#C4A76C] text-white transition-all group shadow-lg"
                >
                  <Send className="mr-2 group-hover:translate-x-1 transition-transform" size={18} />
                  Send Message
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;